<template>
  <div class="d-flex flex-column gap-7 gap-lg-10">
    <div class="d-flex flex-column flex-xl-row gap-7 gap-lg-10">
      <div class="post d-flex flex-column-fluid" id="kt_post">
        <!--begin::Container-->
        <div id="kt_content_container" class="container-xxl">
          <!--begin::div-->
          <div id="kt_ecommerce_add_product_form" class="form d-flex flex-column flex-lg-row">
            
            <!--begin::Main column-->
            <div class="d-flex flex-column flex-row-fluid gap-7 gap-lg-10" v-loading="fullscreenLoading">
              <!--begin::General-->
              <div class="card" v-if="orderProductDetails.value">
                <div class="card-header border-0 pt-6">
                  <div class="d-flex flex-column">
                    <div class="d-flex align-items-center mb-2 float-left">
                      <span class="text-gray-900 fs-2 fw-bolder me-1">{{ orderProductDetails.value.product_name }}</span>
                    </div>
                    <div class="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2 float-left">
                      <span class="d-flex align-items-center text-gray-400 me-5 mb-2">{{ orderProductDetails.value.product_sku }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex flex-column flex-xl-row gap-7 gap-lg-10" v-if="orderProductDetails.value && orderProductDetails.value.order_summary">
                <div class="card card-flush py-4 flex-row-fluid">
                  <div class="card-header">
                    <div class="card-title">
                      <h2>Order Details</h2>
                    </div>
                    <div class="card-toolbar">
                      <a href="javascript:void(0)" class="btn btn-sm btn-light-primary" @click.prevent="openOrderDetailPage(orderProductDetails.value.order_summary.id)">View Full Order</a>
                    </div>
                  </div>
                  <div class="card-body pt-0">
                    <div class="table-responsive">
                      <table class="table align-middle table-row-bordered mb-0 fs-6 gy-5 min-w-300px">
                        <tbody class="fw-bold text-gray-600">
                          <tr v-if="orderProductDetails.value.order_summary.id">
                            <td class="text-muted">System Order Id</td>
                            <td class="fw-bolder text-end">#{{ orderProductDetails.value.order_summary.id }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.order_id">
                            <td class="text-muted">Partner Order Id</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.order_id }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.orderstatus || orderProductDetails.value.order_summary.order_status">
                            <td class="text-muted">Order Status</td>
                            <td class="fw-bolder text-end">
                              <span
                                v-if="orderProductDetails.value.order_summary.orderstatus"
                                :class="`badge badge-light-${orderProductDetails.value.order_summary.orderstatus.badge}`"
                              >
                                {{ orderProductDetails.value.order_summary.orderstatus.title }}
                              </span>
                              <span v-else>{{ orderProductDetails.value.order_summary.order_status }}</span>
                            </td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.order_date">
                            <td class="text-muted">Order Date</td>
                            <td class="fw-bolder text-end">{{ formatDateTime(orderProductDetails.value.order_summary.order_date) }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.order_due_date">
                            <td class="text-muted">Order Due Date</td>
                            <td class="fw-bolder text-end">{{ formatDate(orderProductDetails.value.order_summary.order_due_date) }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.shipping_method">
                            <td class="text-muted">Shipping Method</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.shipping_method }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.shipping_type">
                            <td class="text-muted">Shipping Type</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.shipping_type }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.invoice_number">
                            <td class="text-muted">Invoice Number</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.invoice_number }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.order_type !== null && orderProductDetails.value.order_summary.order_type !== undefined">
                            <td class="text-muted">Order Type</td>
                            <td class="fw-bolder text-end">{{ getOrderTypeLabel(orderProductDetails.value.order_summary.order_type) }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.is_order_synced_with_shipstation !== null && orderProductDetails.value.order_summary.is_order_synced_with_shipstation !== undefined">
                            <td class="text-muted">ShipStation Sync</td>
                            <td class="fw-bolder text-end">
                              <span :class="orderProductDetails.value.order_summary.is_order_synced_with_shipstation ? 'badge badge-light-success' : 'badge badge-light-secondary'">
                                {{ orderProductDetails.value.order_summary.is_order_synced_with_shipstation ? 'Synced' : 'Not Synced' }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div class="card card-flush py-4 flex-row-fluid">
                  <div class="card-header">
                    <div class="card-title">
                      <h2>Customer & Partner</h2>
                    </div>
                  </div>
                  <div class="card-body pt-0">
                    <div class="table-responsive">
                      <table class="table align-middle table-row-bordered mb-0 fs-6 gy-5 min-w-300px">
                        <tbody class="fw-bold text-gray-600">
                          <tr v-if="orderProductDetails.value.order_summary.customer_name">
                            <td class="text-muted">Customer</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.customer_name }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.customer_email">
                            <td class="text-muted">Email</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.customer_email }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.customer_phone">
                            <td class="text-muted">Phone</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.customer_phone }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.customer_company">
                            <td class="text-muted">Company</td>
                            <td class="fw-bolder text-end">{{ orderProductDetails.value.order_summary.customer_company }}</td>
                          </tr>
                          <tr v-if="orderProductDetails.value.order_summary.trading_platforms || orderProductDetails.value.order_summary.trading_partner">
                            <td class="text-muted">Partner</td>
                            <td class="fw-bolder text-end">
                              <div class="d-flex align-items-center justify-content-end gap-2">
                                <el-tooltip
                                  v-if="orderProductDetails.value.order_summary.trading_partner"
                                  class="box-item"
                                  effect="dark"
                                  :content="((orderProductDetails.value.order_summary.trading_platforms && orderProductDetails.value.order_summary.trading_platforms.platform_name) ? orderProductDetails.value.order_summary.trading_platforms.platform_name + '<br>' : '') + ((orderProductDetails.value.order_summary.trading_partner.user && orderProductDetails.value.order_summary.trading_partner.user.name) ? '(' + orderProductDetails.value.order_summary.trading_partner.user.name + ')' : '')"
                                  placement="top"
                                  raw-content
                                >
                                  <img
                                    :src="setImage(orderProductDetails.value.order_summary.trading_partner.partner_image_path)"
                                    class="w-25px ms-n1 rounded-circle"
                                    :alt="(orderProductDetails.value.order_summary.trading_platforms && orderProductDetails.value.order_summary.trading_platforms.platform_name) ? orderProductDetails.value.order_summary.trading_platforms.platform_name : ''"
                                  >
                                </el-tooltip>
                                <span>
                                  {{ orderProductDetails.value.order_summary.trading_partner && orderProductDetails.value.order_summary.trading_partner.user ? orderProductDetails.value.order_summary.trading_partner.user.name : '-' }}
                                  <span v-if="orderProductDetails.value.order_summary.trading_platforms && orderProductDetails.value.order_summary.trading_platforms.platform_name">
                                    ({{ orderProductDetails.value.order_summary.trading_platforms.platform_name }})
                                  </span>
                                </span>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card card-flush py-4">
                <!--begin::Card body-->
                <div class="card-body pt-0">
                  <div class="mb-10 fv-row">
                    <table class="table table-flush fw-bold gy-1">
                      <tr>
                        <td width="20%" style="vertical-align: top;">
                           <h2>Preview</h2>
                          <el-image v-if="orderProductDetails.value && orderProductDetails.value.product_image && orderProductDetails.value.product_image.length" style="width: 220px; height: 220px" :src="orderProductDetails.value.product_image[0]" :preview-src-list="orderProductDetails.value.product_image" :initial-index="0" fit="cover" class="w-220px h-250px">
                            <template #error>
                              <div class="image-slot">
                                <el-image style="width: 220; height: 220" :src="'/media/no-img.jpg'" fit="cover" />
                              </div>
                            </template>
                          </el-image>
                          <div v-if="orderProductDetails.value && orderProductDetails.value.preview && orderProductDetails.value.preview.source_art_url && orderProductDetails.value.preview.image_source === 'print_file'" class="mt-4">
                            <h2 class="fs-6">Source art</h2>
                            <el-image style="width: 160px; height: 160px" :src="orderProductDetails.value.preview.source_art_url" :preview-src-list="[orderProductDetails.value.preview.source_art_url]" fit="cover" />
                          </div>
                          <div v-if="orderProductDetails.value && orderProductDetails.value.preview && orderProductDetails.value.preview.print_preview_url && orderProductDetails.value.preview.image_source === 'source_art'" class="mt-4">
                            <h2 class="fs-6">Print-ready (Fujifilm)</h2>
                            <el-image style="width: 160px; height: 160px" :src="orderProductDetails.value.preview.print_preview_url" :preview-src-list="[orderProductDetails.value.preview.print_preview_url]" fit="cover" />
                          </div>
                          <div v-if="orderProductDetails.value && orderProductDetails.value.preview && orderProductDetails.value.preview.print_file_status" class="mt-3 fs-7">
                            <span class="badge badge-light-info me-1">Print: {{ orderProductDetails.value.preview.print_file_status }}</span>
                            <span v-if="orderProductDetails.value.preview.fulfillment && orderProductDetails.value.preview.fulfillment.status" :class="orderProductDetails.value.preview.fulfillment.status === 'failed' ? 'badge badge-light-danger' : 'badge badge-light-primary'">
                              Fujifilm: {{ orderProductDetails.value.preview.fulfillment.status }}
                            </span>
                          </div>
                          <div v-if="orderProductDetails.value && orderProductDetails.value.preview && orderProductDetails.value.preview.fulfillment && orderProductDetails.value.preview.fulfillment.error_message" class="mt-2 fs-7 text-danger">
                            {{ orderProductDetails.value.preview.fulfillment.error_message }}
                          </div>
                        </td>
                        <td width="80%" style="vertical-align: top;">
                          <table class="table table-flush fw-bold gy-1">
                            <tbody>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.reference_sku_code">
                                <td class="text-muted min-w-200px w-200px">Product SKU Mapped with : </td>
                                <td class="text-gray-800">
                                  <a v-if="orderProductDetails.value.mapped_product_sku && orderProductDetails.value.mapped_product_sku.product_id" target="_blank" :href=" '/products/view/' + orderProductDetails.value.mapped_product_sku.product_id"> {{orderProductDetails.value.reference_sku_code}} </a>
                                  <span v-else> {{orderProductDetails.value.reference_sku_code}} </span>
                                </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.reference_art_sku_code">
                                <td class="text-muted min-w-200px w-200px">Art SKU Mapped with : </td>
                                <td class="text-gray-800">
                                  <a v-if="orderProductDetails.value.art_sku_id" href="javascript:void(0);" target="_blank" @click="$router.push({ name: 'edit-art-sku', params: { id: orderProductDetails.value.art_sku_id } })">{{orderProductDetails.value.reference_art_sku_code}}</a>
                                  <span v-else> {{orderProductDetails.value.reference_art_sku_code}} </span>
                                </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_size_label">
                                <td class="text-muted min-w-200px w-200px">Size Label : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_size_label}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_quantity">
                                <td class="text-muted min-w-200px w-200px">Quantity : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_quantity}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_width">
                                <td class="text-muted min-w-200px w-200px">Width : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_width}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_height">
                                <td class="text-muted min-w-200px w-200px">Height : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_height}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_weight">
                                <td class="text-muted min-w-200px w-200px">Weight : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_weight}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_unit">
                                <td class="text-muted min-w-200px w-200px">Unit : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_unit}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_price">
                                <td class="text-muted min-w-200px w-200px">Price : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_price}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_shipping_price">
                                <td class="text-muted min-w-200px w-200px">Shipping Price : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_shipping_price}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_discount">
                                <td class="text-muted min-w-200px w-200px">Discount : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_discount}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_tax">
                                <td class="text-muted min-w-200px w-200px">Tax : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_tax}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_subtotal">
                                <td class="text-muted min-w-200px w-200px">Sub Total : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_subtotal}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_production_days">
                                <td class="text-muted min-w-200px w-200px">Production Days : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_production_days}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.product_shipping_days">
                                <td class="text-muted min-w-200px w-200px">Shipping Days : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.product_shipping_days}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.order_product_status && orderProductDetails.value.order_product_status.title">
                                <td class="text-muted min-w-200px w-200px">Status : </td>
                                <td class="text-gray-800"> <span :class="'badge badge-light-'+ orderProductDetails.value.order_product_status.badge">{{orderProductDetails.value.order_product_status.title}} </span></td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.production_due_date">
                                <td class="text-muted min-w-200px w-200px">Due Date : </td>
                                <td class="text-gray-800"> {{orderProductDetails.value.production_due_date}} </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.order_product_status && orderProductDetails.value.order_shipment && orderProductDetails.value.order_product_status.type == 'shipped'">
                                <td colspan="2">
                                  <table width="100%">
                                    <tr>
                                      <td class="text-muted min-w-200px w-200px">Shipping Method : </td>
                                      <td class="text-gray-800">{{ orderProductDetails.value.order_shipment.shipping_method }}</td>
                                    </tr>
                                    <tr>
                                      <td class="text-muted min-w-200px w-200px">Shipping Type : </td>
                                      <td class="text-gray-800">{{ orderProductDetails.value.order_shipment.shipping_type }}</td>
                                    </tr>
                                    <tr v-if="internalPackage.length > 0">
                                      <td class="text-muted min-w-200px w-200px">Internal Package : </td>
                                      <td>
                                        <table width="50%" class="border p-2">
                                          <tr>
                                            <td>Package</td>
                                            <td>Weight</td>
                                            <td>Tracking No.</td>
                                          </tr>
                                          <tr v-for="(val, key) in internalPackage" :key="key">
                                            <td>{{ val.package }}</td>
                                            <td>{{ val.weight }}</td>
                                            <td>{{ val.tracking_no }}</td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                    <tr v-if="externalPackage.length > 0">
                                      <td class="text-muted min-w-200px w-200px">Extenal Package : </td>
                                      <td>
                                        <table width="50%" class="border p-2">
                                          <tr>
                                            <td>Package</td>
                                            <td>Weight</td>
                                            <td>Tracking No.</td>
                                          </tr>
                                          <tr v-for="(val, key) in externalPackage" :key="key">
                                            <td>{{ val.package }}</td>
                                            <td>{{ val.weight }}</td>
                                            <td>{{ val.tracking_no }}</td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  </table>
                                </td>
                              </tr>
                              <tr v-if="orderProductDetails.value && orderProductDetails.value.downloaded_files && orderProductDetails.value.downloaded_files != ''">
                                <td class="text-muted">Product Files : </td>
                                <td>
                                  <div v-if="orderProductDetails.value && orderProductDetails.value.downloaded_files.success && orderProductDetails.value.downloaded_files.success['P'+orderProductDetails.value.product_id]">
                                    <tr>
                                      <td class="text-gray-800"> 
                                        Successfully Downloaded 
                                      </td>
                                    </tr>
                                    <tr v-for="(files, index) in orderProductDetails.value.downloaded_files.success['P'+orderProductDetails.value.product_id]" :key="index">
                                      <td class="text-gray-800"> 
                                          <tr>
                                            <td class="text-gray-800"> 
                                            {{formatText(index)}} : 
                                            </td>
                                          </tr>
                                          <tr>
                                            <td style="padding-left:10px">
                                              <tr class="text-gray-800" v-for="(fileName, fileKey) in files" :key="fileKey">
                                                <template v-if="fileKey != 'usage'">
                                                  <td class="text-gray-800"> 
                                                      {{formatText(fileKey)}} : 
                                                  </td>
                                                  <td class="text-gray-800"> 
                                                    <template v-if="typeof fileName === 'object'">
                                                        <span v-for="(item, itemkey) in fileName" :key="itemkey">
                                                            <a href="javascript:void(0)" @click.prevent="downloadFile('extrafiles/' + item)"> {{itemkey}}</a><br>
                                                        </span>
                                                    </template>
                                                    <template v-else>
                                                        <a href="javascript:void(0)" @click.prevent="downloadFile(fileName)">{{fileName}}</a>
                                                    </template>
                                                  </td>
                                                </template>
                                              </tr>
                                            </td>
                                          </tr>
                                      </td>
                                    </tr>
                                  </div>
                                  <div v-if="orderProductDetails.value && orderProductDetails.value.downloaded_files && orderProductDetails.value.downloaded_files.failed">
                                    <tr>
                                      <td class="text-gray-800"> 
                                        Failed to Download 
                                      </td>
                                    </tr>
                                    <tr v-for="(files, index) in orderProductDetails.value.downloaded_files.failed['P'+orderProductDetails.value.product_id]" :key="index">
                                      <td class="text-gray-800"> 
                                          <tr>
                                            <td class="text-gray-800"> 
                                            {{formatText(index)}} : 
                                            </td>
                                          </tr>
                                          <tr>
                                            <td style="padding-left:10px">
                                              <tr class="text-gray-800" v-for="(fileName, fileKey) in files" :key="fileKey">
                                                <template v-if="fileKey != 'usage'">
                                                  <td class="text-gray-800"> 
                                                      {{formatText(fileKey)}} : 
                                                  </td>
                                                  <td class="text-gray-800"> 
                                                    <template v-if="typeof fileName === 'object'">
                                                        <span v-for="(item, itemkey) in fileName" :key="itemkey">
                                                            <a :href="itemkey" target="_blank"> {{itemkey}}</a><br>
                                                        </span>
                                                    </template>
                                                    <template v-else>
                                                        <a :href="fileName" target="_blank">{{fileName}}</a>
                                                    </template>
                                                  </td>
                                                </template>
                                              </tr>
                                            </td>
                                          </tr>
                                      </td>
                                    </tr>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
                <!--end::Card header-->
              </div>
              <!--end::General-->
              <!--begin::Extra fields-->
              <div class="card card-flush py-4" v-if="orderProductDetails.value && orderProductDetails.value.extra_fields">
                <!--begin::Card header-->
                <div class="card-header">
                  <div class="card-title">
                    <h2>Extra Fields</h2>
                  </div>
                </div>
                <!--end::Card header-->
                <!--begin::Card body-->
                <div class="card-body pt-0">
                  <div class="mb-10 fv-row">
                    <table class="table table-flush fw-bold gy-1">
                      <tbody>
                        <template v-for="(value, key) in orderProductDetails.value.extra_fields" :key="key"> 
                          <tr v-if="value && value != null && value != ''">
                            <td class="text-muted min-w-200px w-200px">{{formatText(key)}} : </td>
                            <td class="text-gray-800">
                              <template v-if="typeof value === 'object'">
                                <span v-for="(item, itemkey) in value" :key="itemkey">
                                  {{formatText(itemkey)}} : {{(item) ? item : '-'}} <br>
                                </span>
                              </template>
                              <template v-else>
                                {{value}}
                              </template>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
                <!--end::Card header-->
              </div>
              <!--end::Extra fields-->
            </div>
            <!--end::Main column-->
          </div>
          <!--end::div-->
        </div>
        <!--end::Container-->
      </div>
    </div>
  </div>
</template>
<script>
import { reactive, ref, onMounted } from "vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import { useRoute } from "vue-router";
import { formatPrice, formatDate, formatDateTime } from "@/core/helpers/common";
import { useRouter } from "vue-router";
import { notificationFire } from "@/composable/notification.js";
import useEventBus from '../../composable/useEventBus'
import { setCurrentPageActionButtons } from "@/core/helpers/actionButtons";
import globalConstant from "@/core/data/globalConstant.js";
import { formatText } from "@/core/helpers/common";

import {
  createOrderPoductOptions,
  createOrderPoductFiles
} from "@/composable/order.js";

export default {
  name: "Order Product Details",
  setup() {
    const { onEvent } = useEventBus()
    const router = useRouter();
    const orderId = ref(0);
    const orderProductId = ref(0);
    const orderProductDetails = reactive({});
    const route = useRoute();
    const fullscreenLoading = ref(false);
    const constants = globalConstant;
    const internalPackage = ref([]);
    const externalPackage = ref([]);

    let fromRoutePath = "orders";
    let fromRouteName = "orders";
    let fromRoutePathDetails = "order-details";
    let fromRouteNameDetails = "order-details";
    if(route.name == "orderProduct"){
        fromRoutePath = "order-products";
        fromRouteName = "order-products";
        fromRoutePathDetails = "order-products";
        fromRouteNameDetails = "Order Details";
    }

    setCurrentPageBreadcrumbs(formatText(route.name),  [{"name" :formatText(fromRouteName), 'path': fromRoutePath}, {"name" :formatText(fromRouteNameDetails), 'path': fromRoutePathDetails, 'id': orderId.value}, {"name" :"Order Product", active: true}]);

    const getOrderProductDetails = async () => {
      fullscreenLoading.value = true;
      internalPackage.value = [];
      externalPackage.value = [];
      await ApiService.query(
        "orders/product/" + orderId.value + "/" + orderProductId.value
      )
        .then(({ data }) => {
          if (data.data) {
            orderProductDetails.value = data.data;
            if (orderProductDetails.value.order_shipment && orderProductDetails.value.order_shipment.order_shipment_packages) {
              orderProductDetails.value.order_shipment.order_shipment_packages.map(function (element) {
                if (element.package_type == 1) {
                  externalPackage.value.push({
                    package: element.package_length + 'L x ' + element.package_width + 'W x ' + element.package_height + 'H (' + element.length_class + ')',
                    weight: element.weight + ' ' + element.weight_class,
                    tracking_no: (element.tracking_no != null) ? element.tracking_no : '-'
                  });
                } else {
                  internalPackage.value.push({
                    package: element.package_length + 'L x ' + element.package_width + 'W x ' + element.package_height + 'H (' + element.length_class + ')',
                    weight: element.weight + ' ' + element.weight_class,
                    tracking_no: (element.tracking_no != null) ? element.tracking_no : '-'
                  });
                }
              })
            }
            let orderfileData = JSON.parse(JSON.stringify(orderProductDetails.value));
            orderfileData.downloaded_files = JSON.stringify(orderProductDetails.value.downloaded_files);
            orderProductDetails.value["product_image"] = createOrderPoductFiles(orderfileData)["productFilesInfo"];
            setCurrentPageBreadcrumbs(formatText(route.name),  [{"name" :formatText(fromRouteName), 'path': fromRoutePath}, {"name" :formatText(fromRouteNameDetails), 'path': fromRoutePathDetails, 'id': orderId.value}, {"name" :"Order Product", active: true}]);
          }
          fullscreenLoading.value = false;
        })
        .catch((error) => {
          fullscreenLoading.value = false;
          let response = error.response;
          if (response) {
            let status = response.status;
            let message = response.data.message;
            if (status == 404) {
              router.push({ name: fromRouteName });
            } else {
              notificationFire(message, "error");
            }
          }
        });
    };

    //Get click event of toolbar buttons
    onEvent("actionName", (actionName) => {
      if (actionName == "back") {
        router.back();
      }
    });

    const downloadFile = (fileName) => {
        if(fileName != ''){
            window.open(process.env.VUE_APP_API_URL + '../../../orders/' + orderId.value + '/' +fileName, '_blank');
        }
    }

    const openOrderDetailPage = (selectedOrderId) => {
      router.push('/' + fromRoutePath + '/' + selectedOrderId);
    };

    const getOrderTypeLabel = (orderType) => {
      if (orderType == 0) {
        return 'Manual';
      }
      if (orderType == '1' || orderType == 1) {
        return 'Online';
      }
      if (orderType == '2' || orderType == 2) {
        return 'Offline';
      }
      return '-';
    };

    const setImage = (image) => {
      if (image == null) {
        return '/media/avatars/blank.png';
      }
      return process.env.VUE_APP_API_URL + "../../../partner_image/" + image;
    };

    onMounted(async () => {
      setCurrentPageActionButtons([{"name" :constants.general.BACK, 'action': 'back','class': 'btn-primary','isLoader': false,'iconClass':"bi bi-arrow-left fs-4" }])
      orderId.value = route.params.order_id;
      orderProductId.value = route.params.product_id;
      await getOrderProductDetails();
    });

    return {
      orderId,
      orderProductId,
      orderProductDetails,
      formatPrice,
      formatDate,
      formatDateTime,
      fullscreenLoading,
      formatText,
      downloadFile,
      internalPackage,
      externalPackage,
      openOrderDetailPage,
      getOrderTypeLabel,
      setImage
    };
  },
};
</script>